import {
  CHANGE_EVENT as PREF_CHANGE_EVENT,
  PREFIX as STORAGE_PREFIX,
  readPref,
  writePref,
} from '../lib/prefs.js';

/**
 * LA MEMOIRE DU "NOUVEAU" : ce que ce poste a deja vu, et depuis quand.
 *
 * Modele mental : la boite mail. Une fiche jamais ouverte se voit au premier
 * coup d'oeil et s'eteint des qu'on l'ouvre. Deux valeurs suffisent, par app +
 * ecran + PORTE (un jeu de donnees ne dit rien d'un autre) :
 *
 * 1. la DATE DE REFERENCE (`since`), posee a la premiere visite de la porte et
 *    figee ensuite. Sans elle, la premiere ouverture allumerait les 500 lignes
 *    deja en base : "tout est nouveau" ne veut rien dire ;
 * 2. l'ENSEMBLE DES IDS DEJA OUVERTS (`seen`) depuis cette date.
 *
 * 100% LOCAL AU POSTE, ZERO ecriture en base, zero regle de securite, zero
 * champ de modele : ca marche ce soir, et la limite est assumee (sur un autre
 * ordinateur, tout redevient nouveau). Le vrai multi-utilisateur ("si Viviane
 * ouvre une fiche, elle reste nouvelle pour Didier") a sa propre fiche,
 * `nouveau-multi-utilisateur`, et elle n'est PAS anticipee ici.
 *
 * C'EST POURQUOI `readNewMemory` ET `writeNewMemory` SONT LES DEUX SEULS
 * POINTS DE LECTURE ET D'ECRITURE DU STOCKAGE. Tout le reste du produit
 * (`markRecordRead`, `useScreen`, le reglage) passe par eux. Le jour ou la
 * memoire deviendra partagee (Firestore, prefs utilisateur), ce chantier-la
 * n'aura que ces DEUX fonctions a remplacer, pas quinze appels disperses.
 * `forgetNewMemory` est la seule exception assumee, et sa raison est ecrite
 * sur elle : elle balaye et repose des cles qu'elle ne connait pas d'avance,
 * ce que `lib/prefs.ts` ne sait pas faire (il n'enumere pas).
 *
 * `markSeen` n'est PAS une exception : elle ne touche a rien, elle CALCULE.
 * C'est la regle de file, pure, pour que personne n'ait a relire le stockage
 * pour connaitre un etat qu'il vient de produire.
 *
 * Le stockage lui-meme est celui des prefs (`lib/prefs.ts`, localStorage
 * "taki.prefs.<cle>"), qui retombe DEJA en silence sur son repli quand
 * localStorage est absent ou plein (mode prive, quota). Consequence assumee et
 * a dire au fondateur : dans ce cas, rien ne se memorise, donc les nouveautes
 * restent allumees d'une session a l'autre. Jamais d'exception qui remonte.
 */

/**
 * PLAFOND DUR de l'ensemble des ids deja ouverts. Sa raison d'etre est intacte
 * et n'a jamais bouge : un ensemble qui grossit sans limite finit par saturer
 * le stockage local du navigateur, sans erreur, en silence, et c'est le genre
 * de panne qu'on ne diagnostique jamais.
 *
 * DECOUPLE DE LA FENETRE D'AFFICHAGE, volontairement. Le brief du chantier
 * disait 500, et justifiait ce chiffre par le plafond de fenetre du mode
 * client (`DEFAULT_LIMIT`, useScreen.ts). C'etait exactement la cause du
 * defaut : caler la memoire sur la fenetre, c'est garantir qu'elle ne peut pas
 * couvrir un seul ecran plein de nouveautes. 600 fiches importees, traitees a
 * la fleche Suivant : a la 501e, la premiere lue tombait de l'ensemble et se
 * rallumait SOUS LES YEUX de l'utilisateur, alors qu'elle etait encore a
 * l'ecran. Le plafond doit donc etre NETTEMENT AU-DESSUS de la plus grande
 * fenetre possible (`MAX_CLIENT_WINDOW`, 1000 dans useScreen.ts), jamais egal
 * a elle.
 *
 * 5000, parce que le cout reste negligeable : un id Firestore fait 20
 * caracteres, plus les guillemets et la virgule du JSON, soit de l'ordre de
 * 115 Ko par porte, tres loin du quota de localStorage (5 Mo par origine dans
 * tous les navigateurs courants).
 */
export const NEW_SEEN_MAX = 5000;

/** Ce qu'un poste retient d'une porte. */
export interface NewMemory {
  /**
   * Date de reference de la porte, en millisecondes. `null` = jamais visitee,
   * donc rien n'est encore nouveau (on ne sait pas depuis quand compter).
   */
  since: number | null;
  /**
   * Les ids deja ouverts, DU PLUS ANCIENNEMENT LU AU PLUS RECEMMENT LU.
   * L'ordre n'est pas cosmetique : c'est lui qui decide qui tombe au plafond.
   */
  seen: readonly string[];
}

/** Le suffixe qui identifie une cle de memoire du "nouveau". */
const MEMORY_SUFFIX = '.nouveau';

/** Une porte jamais visitee : rien de nouveau, rien de lu. */
const EMPTY_MEMORY: NewMemory = { since: null, seen: [] };

/**
 * Cle de la memoire d'une porte. Meme forme que `favoritesPrefKey` (app,
 * ecran, porte), et pour la meme raison : ce qu'on a lu dans un jeu de donnees
 * ne dit rien de ce qu'on a lu dans un autre. L'ecran sans portes range la
 * sienne sous la porte implicite (`IMPLICIT_DOOR_ID`).
 */
export function newMemoryPrefKey(appId: string, screenId: string, doorId: string): string {
  return `app.${appId}.${screenId}.${doorId}${MEMORY_SUFFIX}`;
}

/**
 * Cle du REGLAGE (allumer ou eteindre le reperage). Par APP, jamais par ecran :
 * c'est un choix de confort de l'utilisateur sur une app entiere, pas un
 * reglage d'affichage d'un ecran. Cle absente = ALLUMEE.
 */
export function newBadgePrefKey(appId: string): string {
  return `app.${appId}${MEMORY_SUFFIX}`;
}

/**
 * Remet d'aplomb une valeur stockee ABIMEE : JSON d'une autre version, `seen`
 * qui n'est pas un tableau, ids qui ne sont pas des chaines, `since` qui n'est
 * pas un nombre. Une pref est du JSON ecrit par n'importe quelle version
 * passee (ou par une extension, ou a la main) : elle se nettoie, elle ne se
 * propage pas, et elle ne fait jamais planter l'ecran qui la lit.
 */
function sanitizeMemory(value: unknown): NewMemory {
  if (value == null || typeof value !== 'object') return EMPTY_MEMORY;
  const raw = value as { since?: unknown; seen?: unknown };
  const since =
    typeof raw.since === 'number' && Number.isFinite(raw.since) && raw.since > 0
      ? raw.since
      : null;
  const seen = Array.isArray(raw.seen)
    ? raw.seen.filter((id: unknown): id is string => typeof id === 'string' && id !== '')
    : [];
  return { since, seen: capSeen(seen) };
}

/**
 * Le PLAFOND, applique en FILE : on jette par la TETE, c'est-a-dire les ids lus
 * il y a le plus longtemps.
 *
 * POURQUOI CET ORDRE-LA, et pas l'inverse. Un id qui tombe de l'ensemble peut
 * redevenir "nouveau" (sa date de creation est toujours posterieure a la date
 * de reference) : une fiche qui se rallume toute seule est LE bug qui detruit
 * la confiance dans cette fonctionnalite. On sacrifie donc ce qui a le moins
 * de chances d'etre encore a l'ecran : les lectures les plus vieilles. Jeter
 * les plus recentes rallumerait la fiche qu'on vient de fermer, sous les yeux
 * de l'utilisateur.
 */
function capSeen(seen: readonly string[]): string[] {
  return seen.length > NEW_SEEN_MAX ? seen.slice(seen.length - NEW_SEEN_MAX) : [...seen];
}

/**
 * LECTURE, point de passage unique. Toute valeur absente, illisible ou abimee
 * rend la memoire vide : une porte jamais visitee.
 */
export function readNewMemory(appId: string, screenId: string, doorId: string): NewMemory {
  return sanitizeMemory(readPref<unknown>(newMemoryPrefKey(appId, screenId, doorId), null));
}

/**
 * ECRITURE, point de passage unique. La valeur est nettoyee et plafonnee ICI,
 * jamais chez l'appelant : c'est ce qui garantit qu'aucun chemin ne peut faire
 * grossir l'ensemble au-dela de `NEW_SEEN_MAX`.
 */
export function writeNewMemory(
  appId: string,
  screenId: string,
  doorId: string,
  memory: NewMemory,
): void {
  const clean = sanitizeMemory(memory);
  writePref<NewMemory>(newMemoryPrefKey(appId, screenId, doorId), clean);
}

/**
 * LA REGLE DE FILE, PURE : la memoire d'apres avoir lu `id`. Aucune lecture,
 * aucune ecriture, aucun `window` : on la donne une memoire, elle en rend une.
 *
 * L'id part en QUEUE, et une fiche deja lue y REMONTE (on la retire d'abord).
 * Ce n'est pas un detail : sans ce deplacement, une fiche ouverte tous les
 * jours garderait sa place d'origine dans la file et finirait par tomber au
 * plafond, donc par se rallumer alors que c'est justement celle que
 * l'utilisateur consulte le plus. La file classe par DERNIERE LECTURE, pas par
 * premiere.
 *
 * ELLE REND L'OBJET RECU, TEL QUEL, quand il n'y a rien a faire (id vide, ou
 * deja le dernier lu : rouvrir la meme fiche, revenir dessus a la fleche).
 * C'est un contrat, pas un hasard : l'appelant compare PAR IDENTITE
 * (`next === memoire`) pour savoir s'il doit ecrire. Sans ca, le cas le plus
 * courant du produit couterait une ecriture localStorage et un rendu.
 *
 * POURQUOI PURE ET EXPORTEE. `useScreen` tient la memoire en etat React : s'il
 * devait relire le stockage pour connaitre l'etat d'apres, une ecriture qui a
 * echoue en silence (mode prive, quota) lui rendrait une memoire VIDE, qui
 * ecraserait son etat et eteindrait toutes les pastilles de l'ecran d'un seul
 * clic. On ne relit jamais le stockage pour un etat qu'on vient de calculer.
 * `markRecordRead` passe par ici lui aussi : UNE seule regle de file, ecrite
 * UNE seule fois.
 */
export function markSeen(memory: NewMemory, id: string): NewMemory {
  if (id === '') return memory;
  if (memory.seen[memory.seen.length - 1] === id) return memory;
  const seen = memory.seen.filter((seenId) => seenId !== id);
  seen.push(id);
  return { since: memory.since, seen: capSeen(seen) };
}

/**
 * Marque une fiche comme LUE sur ce poste : lecture, regle de file, ecriture.
 *
 * Le chemin des appelants HORS React (et de tout ce qui n'a pas la memoire
 * sous la main). Dans `useScreen`, c'est `markSeen` qu'on appelle directement,
 * sur l'etat deja en memoire : voir la raison sur `markSeen`.
 */
export function markRecordRead(
  appId: string,
  screenId: string,
  doorId: string,
  id: string,
): void {
  const memory = readNewMemory(appId, screenId, doorId);
  const next = markSeen(memory, id);
  // Identite : `markSeen` rend l'objet recu quand il n'y a rien a faire.
  if (next === memory) return;
  writeNewMemory(appId, screenId, doorId, next);
}

/**
 * "TOUT MARQUER COMME LU" : repose la date de reference de TOUTES les portes de
 * cet ecran a MAINTENANT, et vide leur ensemble d'ids. Sans ce geste, une liste
 * qui a pris 200 entrees d'un coup ne se rattrape qu'en ouvrant 200 fiches.
 *
 * TOUTES LES PORTES, parce que le reglage ne connait pas les portes : il vit
 * dans le hub Parametres, loin de l'ecran, et ne peut pas savoir laquelle
 * l'utilisateur regardait. On balaye donc les cles par PREFIXE, ce qui est le
 * seul endroit du module qui touche `localStorage` autrement que par
 * `readNewMemory` / `writeNewMemory` : `lib/prefs.ts` ne sait pas enumerer, et
 * la liste des portes n'existe pas ici.
 *
 * ON ECRIT `{ since: maintenant, seen: [] }`, ON N'EFFACE PAS. Effacer semblait
 * revenir au meme (une porte sans memoire est une porte jamais visitee, dont la
 * date se repose a la visite suivante), et c'etait faux, parce que "la visite
 * suivante" peut arriver trois jours plus tard : vendredi 18h on clique, un
 * connecteur cree 40 fiches pendant le week-end, lundi 9h la date se pose a
 * lundi 9h, et les 40 fiches du week-end ne sont JAMAIS signalees. La date doit
 * se poser au moment du GESTE, pas au retour de l'utilisateur.
 *
 * L'event "taki:pref-change" est RE-DISPATCHE pour chaque cle traitee : sans
 * lui, les `usePref` montes et le memo de `useScreen` de CE document
 * continueraient d'afficher des pastilles que plus rien ne justifie. C'est
 * exactement ce que fait `writePref`. Rien ne franchit le document : un SECOND
 * ONGLET ne se resynchronise pas (aucun ecouteur "storage" nulle part), il
 * verra le rattrapage a son prochain rechargement.
 *
 * REND LE NOMBRE de cles reellement ecrites, ou `null` quand le balayage n'a
 * pas pu se faire (localStorage indisponible, ou quota atteint au milieu de la
 * boucle). Les deux cas ne se confondent surtout pas : ZERO cle ecrite est un
 * succes ordinaire (l'ecran n'avait jamais ete visite, ou le rattrapage avait
 * deja ete fait), alors que `null` est un echec. Sans cette distinction, le
 * reglage se taisait sur un ecran jamais visite : un bouton qu'on clique et qui
 * ne repond rien, ce que la regle G16 interdit.
 */
export function forgetNewMemory(appId: string, screenId: string): number | null {
  const prefix = `app.${appId}.${screenId}.`;
  const keys: string[] = [];
  // La date de reference du GESTE, la meme pour toutes les portes balayees :
  // c'est ce clic-ci qui fait la coupure, pas la prochaine visite de chacune.
  const reset = JSON.stringify({ since: Date.now(), seen: [] } satisfies NewMemory);
  const written: string[] = [];
  // Le balayage a-t-il seulement PU se faire ? Distinct du nombre de cles
  // ecrites : zero cle n'est pas un echec (ecran jamais visite, ou rattrapage
  // deja fait), alors qu'un stockage indisponible en est un. Sans cette
  // distinction, l'appelant ne peut pas choisir entre confirmer et se taire.
  let balayage = false;
  try {
    const storage = window.localStorage;
    for (let index = 0; index < storage.length; index += 1) {
      const stored = storage.key(index);
      if (stored == null || !stored.startsWith(STORAGE_PREFIX)) continue;
      const key = stored.slice(STORAGE_PREFIX.length);
      // Le prefixe porte l'ECRAN et le suffixe la nature de la cle : la cle du
      // reglage (`app.<appId>.nouveau`) n'a pas de segment d'ecran, elle ne
      // peut donc pas etre emportee par ce balayage.
      if (key.startsWith(prefix) && key.endsWith(MEMORY_SUFFIX)) keys.push(key);
    }
    for (const key of keys) {
      storage.setItem(STORAGE_PREFIX + key, reset);
      // Comptee APRES l'ecriture : un quota atteint en cours de route arrete la
      // boucle, et le nombre rendu dit ce qui a vraiment ete fait.
      written.push(key);
    }
    // Pose EN DERNIER, jamais en tete : un quota atteint au milieu de la boucle
    // laisse le rattrapage a moitie fait, et ca n'est pas un succes.
    balayage = true;
  } catch {
    // localStorage indisponible (mode prive, quota) : on ne rattrape rien, et
    // surtout on ne fait rien remonter a l'app. Le `null` rendu le dit.
  }
  for (const key of written) {
    try {
      window.dispatchEvent(new CustomEvent<string>(PREF_CHANGE_EVENT, { detail: key }));
    } catch {
      // Environnement sans CustomEvent : pas de resynchronisation, sans casser.
    }
  }
  return balayage ? written.length : null;
}
